import type { ReactNode } from 'react';

/**
 * Scan from a phone, as one pencil drawing: a hand holds a phone over a slip of paper, the phone's
 * frame finds the page, and the page travels along a dashed line into the job's tray. Line art in the
 * Autopilot cards' style (ExportRoute's AutopilotModeArt); the accent is the action colour, never orange.
 */
export function PhoneScanArt(): ReactNode {
  return (
    <svg className="paige-phone-scan-art" viewBox="0 0 300 140" aria-hidden="true">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <Slip />
        <Phone />
        <path
          className="pa-travel"
          d="M150 44c34-30 82-34 112 0"
          strokeDasharray="3 5"
          opacity=".5"
        />
        <path d="M257 37l6 8-10 1" opacity=".5" />
        <Tray />
      </g>
    </svg>
  );
}

/** The paper on the counter: a slip with a torn foot, a few lines and a total. */
function Slip(): ReactNode {
  return (
    <g transform="rotate(-8 50 92)">
      <path
        d="M18 56h60v66l-6-4-6 4-6-4-6 4-6-4-6 4-6-4-6 4-6-4-6 4Z"
        fill="var(--cp-surface-raised)"
      />
      <path d="M27 68h24M27 77h40M27 85h34M27 93h40" strokeWidth="2" opacity=".4" />
      <path d="M48 106h19" strokeWidth="2.5" opacity=".7" />
    </g>
  );
}

/** The phone over it, the page found in its frame, and the hand that holds it. */
function Phone(): ReactNode {
  return (
    <g>
      <rect x="88" y="12" width="56" height="98" rx="9" fill="var(--cp-surface-raised)" />
      <path d="M109 18h14" opacity=".6" />
      <rect x="94" y="25" width="44" height="74" rx="3" opacity=".5" />
      <path d="M106 41h20v34h-20Z" fill="var(--cp-surface-raised)" />
      <path d="M110 49h12M110 55h9M110 61h12" opacity=".45" />
      <path
        className="pa-frame"
        d="M101 42v-5h6M131 42v-5h-6M101 74v5h6M131 74v5h-6"
        stroke="var(--cp-text-action)"
        strokeWidth="2"
      />
      <circle cx="116" cy="104" r="2.5" opacity=".6" />
      <path
        d="M144 64c8 1 12 7 11 14l-3 22c-1 9-7 15-15 17l-20 4"
        fill="var(--cp-surface-raised)"
      />
      <path d="M88 78c-7 0-10 5-9 10l2 8" />
      <path d="M144 76h7M144 86h6M144 96h4" opacity=".6" />
    </g>
  );
}

/** The job: a tray with the page arriving in it, marked done. */
function Tray(): ReactNode {
  return (
    <g>
      <path className="pa-page" d="M246 56h24l8 8v34h-32Z" fill="var(--cp-surface-raised)" />
      <path d="M252 70h18M252 77h14M252 84h18" opacity=".45" />
      <path d="M232 92h60v28a4 4 0 0 1-4 4h-52a4 4 0 0 1-4-4Z" fill="var(--cp-surface-raised)" />
      <path d="M232 104h16l4 6h20l4-6h16" />
      <circle cx="286" cy="58" r="8" fill="var(--cp-text-action)" stroke="none" />
      <path d="M282 58l3 3 5-6" stroke="var(--cp-surface-raised)" strokeWidth="2" />
    </g>
  );
}
